import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserNotifications,
  markAsRead,
  deleteNotificationThunk,
} from "../store/slices/notificationSlice";
import { checkActiveUser } from "../store/slices/authSlice";
import {
  Check,
  Trash2,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  Bell,
  Filter,
} from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";

const Notifications = () => {
  const dispatch = useDispatch();
  const { activeUser, isAuthenticated } = useSelector((state) => state.auth);
  const { notifications, error, fetchedAt } = useSelector((state) => state.notifications);
  const { theme } = useSidebarContext();
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const notificationsPerPage = 8;

  useEffect(() => {
    // Avoid dispatching checkActiveUser if already present
    if (!activeUser && isAuthenticated) {
      dispatch(checkActiveUser());
    }
  }, [dispatch, activeUser, isAuthenticated]);

  useEffect(() => {
    // Fetch only if authenticated, user id exists, and cache is stale (>5m)
    const isFresh = fetchedAt && Date.now() - fetchedAt < 5 * 60 * 1000;
    if (isAuthenticated && activeUser?.id && !isFresh) {
      dispatch(getUserNotifications(activeUser.id));
    }
  }, [dispatch, activeUser, isAuthenticated, fetchedAt]);

  const handleRefreshNotifications = async () => {
    if (activeUser?.id) {
      setIsRefreshing(true);
      try {
        await dispatch(getUserNotifications(activeUser.id)).unwrap();
      } catch (error) {
        console.error("Failed to refresh notifications", error);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleMarkAsRead = (id) => {
    dispatch(markAsRead(id));
  };

  const handleDelete = (id) => {
    dispatch(deleteNotificationThunk(id));
  };

  const handleMarkAllAsRead = () => {
    notifications.forEach((n) => {
      if (!n.isRead) dispatch(markAsRead(n._id));
    });
  };

  const groupNotificationsByTime = (notifications) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const groupings = {
      all: notifications,
      today: notifications.filter(
        (n) => new Date(n.createdAt).toDateString() === today.toDateString()
      ),
      yesterday: notifications.filter(
        (n) => new Date(n.createdAt).toDateString() === yesterday.toDateString()
      ),
      thisWeek: notifications.filter(
        (n) =>
          new Date(n.createdAt) > new Date(today.setDate(today.getDate() - 7))
      ),
      older: notifications.filter(
        (n) =>
          new Date(n.createdAt) <= new Date(today.setDate(today.getDate() - 7))
      ),
    };

    return groupings;
  };

  const groupedNotifications = groupNotificationsByTime(notifications);
  const filteredNotifications =
    selectedFilter in groupedNotifications
      ? groupedNotifications[selectedFilter]
      : notifications;

  const totalPages = Math.ceil(
    filteredNotifications.length / notificationsPerPage
  );
  const displayedNotifications = filteredNotifications.slice(
    (currentPage - 1) * notificationsPerPage,
    currentPage * notificationsPerPage
  );

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className="flex items-center justify-center p-4 min-h-screen">
      <div
        className={`w-full max-w-6xl ${theme.tailwind.bg32} backdrop-blur-xl rounded-3xl shadow-2xl border ${theme.tailwind.border2} overflow-hidden`}
      >
        {/* Header */}
        <div
          className={`${theme.tailwind.bg55} px-6 py-5 border-b ${theme.tailwind.border3} flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4`}
        >
          <div className="flex items-center space-x-4">
            <div className={`p-2 rounded-lg ${theme.tailwind.bg34} ${theme.tailwind.border2} border`}>
              <Bell className={`${theme.tailwind.text}`} size={24} />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${theme.tailwind.text9}`}>
                Notifications
              </h2>
              <p className={`${theme.tailwind.texting2} text-sm mt-1`}>
                Stay updated with your latest alerts
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefreshNotifications}
              className={`
                ${theme.tailwind.text9} ${theme.tailwind.hover2} transition 
                ${isRefreshing ? "animate-spin" : ""}
              `}
            >
              <RefreshCcw size={20} />
            </button>

            <div className="relative">
              <select
                className={`appearance-none ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg pl-3 pr-8 py-2 focus:ring-2 ${theme.tailwind.focusRing2} transition`}
                value={selectedFilter}
                onChange={(e) => {
                  setSelectedFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                {["all", "today", "yesterday", "thisWeek", "older"].map(
                  (filter) => (
                    <option
                      key={filter}
                      value={filter}
                      className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </option>
                  )
                )}
              </select>
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${theme.tailwind.text9}`}
              >
                <Filter size={16} />
              </div>
            </div>

            <button
              onClick={handleMarkAllAsRead}
              disabled={!notifications.length}
              className={`${theme.tailwind.bg18} ${theme.tailwind.text18} rounded-lg px-4 py-2 ${theme.tailwind.bgHover25} transition disabled:opacity-50`}
            >
              Mark All Read
            </button>
          </div>
        </div>

        {/* Notifications Content */}
        <div className="p-6">
          {isRefreshing && (
            <div
              className={`text-center ${theme.tailwind.text9} animate-pulse`}
            >
              Refreshing notifications...
            </div>
          )}

          {error && (
            <div className="text-red-400 text-center">Error: {error}</div>
          )}

          {!isRefreshing && displayedNotifications.length === 0 && (
            <div className={`text-center ${theme.tailwind.text4}`}>
              No notifications to show
            </div>
          )}

          <div className="space-y-4">
            {displayedNotifications.map((notification) => (
              <div
                key={notification._id}
                className={`
                  p-4 rounded-xl transition transform hover:scale-[1.02] hover:shadow-lg 
                  ${
                    notification.isRead
                      ? `${theme.tailwind.bg17}`
                      : `${theme.tailwind.bg56} border ${theme.tailwind.border11} shadow-md`
                  }
                `}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`${theme.tailwind.text9} font-medium`}>
                      {notification.message}
                    </p>
                    <p className={`text-sm ${theme.tailwind.text4}`}>
                      {notification.type} |{" "}
                      {new Date(notification.createdAt).toLocaleString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        }
                      )}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    {!notification.isRead && (
                      <button
                        onClick={() => handleMarkAsRead(notification._id)}
                        className={`${theme.tailwind.text10} ${theme.tailwind.hover1} transition`}
                      >
                        <Check size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(notification._id)}
                      className="text-red-500 hover:text-red-300 transition"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className={`flex justify-between items-center mt-6 ${theme.tailwind.bg2} rounded-xl p-3`}
            >
              <button
                onClick={() => handlePageChange("prev")}
                disabled={currentPage === 1}
                className={`${theme.tailwind.text9} disabled:opacity-50 ${theme.tailwind.hover} transition`}
              >
                <ChevronLeft size={24} />
              </button>
              <span className={`${theme.tailwind.text9} font-medium`}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange("next")}
                disabled={currentPage === totalPages}
                className={`${theme.tailwind.text9} disabled:opacity-50 ${theme.tailwind.hover} transition`}
              >
                <ChevronRight size={24} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
